export const PAGE_SIZE = 10;
export const ORDER_BY = [{ column: "ROW_ORDER", order: "ASC" }, { column: "ID", order: "ASC" }];

const SNAPSHOT_PAGE_SIZE = 25;
const INT_MIN = -2147483648;
const INT_MAX = 2147483647;
const invalidResponse = () => new Error("APP_SETTINGS.Error_InvalidResponse");
const orderChanged = () => new Error("APP_SETTINGS.Error_OrderChanged");
const validId = (id) => (typeof id === "string" && id.length > 0 && id.trim() === id)
    || (typeof id === "number" && Number.isSafeInteger(id));

const checkRecords = (records, seen = new Set()) => {
    if (!Array.isArray(records)) throw invalidResponse();
    records.forEach((record) => {
        if (!record || !validId(record.id)) throw invalidResponse();
        const id = String(record.id);
        if (seen.has(id)) throw orderChanged();
        seen.add(id);
    });
};

// Pagination checks detect incomplete or changed snapshots, not concurrent writes in general.
// The API has no snapshot token or compare-and-swap revision for ordering.
export const fetchOrderSnapshot = async (client, query, model) => {
    const records = [];
    const seen = new Set();
    let expectedTotal;
    let expectedLastPage;

    for (let page = 1; expectedLastPage === undefined || page <= expectedLastPage; page += 1) {
        const result = await client.query({
            query,
            variables: { first: SNAPSHOT_PAGE_SIZE, page, orderBy: ORDER_BY },
            fetchPolicy: "network-only",
            context: { queryDeduplication: false },
        });
        const data = result?.data?.[model];
        const info = data?.paginatorInfo;
        if (!Array.isArray(data?.data)
            || !Number.isSafeInteger(info?.total) || info.total < 0
            || !Number.isSafeInteger(info?.lastPage) || info.lastPage < 1
            || info?.currentPage !== page) throw invalidResponse();

        if (expectedTotal !== undefined && (info.total !== expectedTotal || info.lastPage !== expectedLastPage)) {
            throw orderChanged();
        }
        if (info.lastPage !== Math.max(1, Math.ceil(info.total / SNAPSHOT_PAGE_SIZE))) throw invalidResponse();
        expectedTotal = info.total;
        expectedLastPage = info.lastPage;

        const expectedLength = Math.min(SNAPSHOT_PAGE_SIZE, Math.max(0, expectedTotal - (page - 1) * SNAPSHOT_PAGE_SIZE));
        if (data.data.length !== expectedLength) throw orderChanged();
        checkRecords(data.data, seen);
        records.push(...data.data);
    }

    return records;
};

export const createOrderUpdates = (snapshot, visibleIds, currentPage, oldIndex, newIndex) => {
    checkRecords(snapshot);
    if (!Array.isArray(visibleIds) || !Number.isInteger(currentPage) || currentPage < 1
        || currentPage > Math.max(1, Math.ceil(snapshot.length / PAGE_SIZE))) throw orderChanged();

    const start = (currentPage - 1) * PAGE_SIZE;
    const currentRecords = snapshot.slice(start, start + PAGE_SIZE);
    if (visibleIds.length !== currentRecords.length
        || visibleIds.some((id, index) => !validId(id) || String(id) !== String(currentRecords[index].id))
        || !Number.isInteger(oldIndex) || !Number.isInteger(newIndex)
        || oldIndex < 0 || newIndex < 0 || oldIndex >= currentRecords.length || newIndex >= currentRecords.length) {
        throw orderChanged();
    }
    if (oldIndex === newIndex) return [];

    const reordered = [...currentRecords];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);
    const target = [...snapshot.slice(0, start), ...reordered, ...snapshot.slice(start + currentRecords.length)];
    return target.flatMap((record, index) => record.row_order === index + 1 ? [] : [{ id: record.id, row_order: index + 1 }]);
};

export const nextRowOrder = (snapshot) => {
    if (!Array.isArray(snapshot)) throw invalidResponse();
    let maximum;
    snapshot.forEach((record) => {
        const value = record?.row_order;
        if (typeof value === "number" && Number.isInteger(value) && (maximum === undefined || value > maximum)) maximum = value;
    });
    const next = (maximum ?? 0) + 1;
    if (!Number.isSafeInteger(next) || next < INT_MIN || next > INT_MAX) throw new Error("APP_SETTINGS.Error_OrderLimit");
    return next;
};

export const findRecordPage = (snapshot, id) => {
    checkRecords(snapshot);
    if (!validId(id)) throw orderChanged();
    const index = snapshot.findIndex((record) => String(record.id) === String(id));
    if (index < 0) throw orderChanged();
    return Math.floor(index / PAGE_SIZE) + 1;
};
